import { captureException, withScope } from "@sentry/nextjs";
import { getClientPlatform } from "@/lib/analytics/platform-context";
import { getOriginalConsoleError } from "@/lib/observability/console-base";
import { normalizeError } from "@/lib/observability/normalize-error";
import { buildClientErrorMetadata } from "@/lib/observability/request-context";
import { sanitizeUnknown } from "@/lib/observability/sanitize";
import { isSentryReportingEnabled } from "@/lib/observability/sentry-env";
import type {
  ClientNetworkContext,
  ErrorReportContext,
  ErrorSeverity,
  ErrorUserContext,
} from "@/lib/observability/types";

let clientNetworkContext: ClientNetworkContext = {};
let clientUserContext: ErrorUserContext = {};

function safeTagValue(value: unknown): string {
  if (value == null) return "unknown";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "[object]";
}

function getClientLog(
  severity: ErrorSeverity | undefined,
): (...args: unknown[]) => void {
  if (severity === "warning") return console.warn.bind(console);
  if (severity === "info") return console.info.bind(console);
  return getOriginalConsoleError();
}

function getClientLogLabel(severity: ErrorSeverity | undefined): string {
  if (severity === "warning") return "[client-warning]";
  if (severity === "info") return "[client-info]";
  return "[client-error]";
}

export function setClientNetworkContext(context: ClientNetworkContext): void {
  clientNetworkContext = { ...clientNetworkContext, ...context };
}

export function setClientErrorUser(user: ErrorUserContext): void {
  clientUserContext = { ...user };
}

export function clearClientErrorUser(): void {
  clientUserContext = {};
}

/**
 * Report an info-level event for the client.
 * Does NOT call captureException — logs to console only (mirrors server-side reportServerInfoEvent).
 */
function reportClientInfoEvent(
  message: string,
  context: Omit<ErrorReportContext, "severity">,
): string | undefined {
  const log = getClientLog("info");
  const label = getClientLogLabel("info");
  const runtime = context.runtime ?? "nextjs-client";
  const handled = context.handled ?? true;
  const expected = context.expected ?? false;

  log(label, message, {
    source: context.source,
    feature: context.feature,
    action: context.action,
    component: context.component,
    runtime,
    handled,
    expected,
    suppressionReason: context.suppressionReason,
    route: context.route,
    method: context.method,
    statusCode: context.statusCode,
  });

  return undefined;
}

export function reportClientError(
  error: unknown,
  context: ErrorReportContext,
): string | undefined {
  const normalizedError = normalizeError(error);

  // Info-severity events should be breadcrumbs, not standalone Sentry issues.
  // Route to reportClientInfoEvent to avoid captureException (mirrors server-side pattern).
  if (context.severity === "info") {
    return reportClientInfoEvent(normalizedError.message, context);
  }

  // REGRESSION GUARD — NODE-1P9 (8 regressions, escalation level 3+)
  // DO NOT add blanket TypeError drops here. Previous nightly runs added
  // "Failed to fetch" / "Load failed" suppression that hid real user-facing
  // failures (502s during deploys, broken saves, thumbnail failures).
  // Errors must flow through to Sentry's beforeSend() in instrumentation-client.ts
  // where endpoint-specific filtering provides nuanced suppression.
  // AbortError is safe to drop — it means the user navigated away intentionally.
  if (normalizedError.name === "AbortError") {
    return undefined;
  }

  const runtime = context.runtime ?? "nextjs-client";
  const handled = context.handled ?? true;
  const expected = context.expected ?? false;
  const mergedUser = {
    id: context.user?.id || clientUserContext.id,
    email: context.user?.email || clientUserContext.email,
  };

  const metadata = buildClientErrorMetadata({
    user: mergedUser,
    network: clientNetworkContext,
    platform: getClientPlatform(),
  });
  const safeExtra = sanitizeUnknown(context.extra || {}) as Record<
    string,
    unknown
  >;
  const safeTags = sanitizeUnknown(context.tags || {}) as Record<
    string,
    unknown
  >;

  const log = getClientLog(context.severity);
  const label = getClientLogLabel(context.severity);
  const summary = [context.source, context.action, normalizedError.message]
    .filter(Boolean)
    .join(" → ");
  log(label, summary, {
    source: context.source,
    feature: context.feature,
    action: context.action,
    component: context.component,
    runtime,
    handled,
    expected,
    suppressionReason: context.suppressionReason,
    route: context.route,
    method: context.method,
    statusCode: context.statusCode,
    metadata,
    error: {
      name: normalizedError.name,
      message: normalizedError.message,
      stack: normalizedError.stack,
    },
    extra: safeExtra,
  });

  if (!isSentryReportingEnabled()) {
    return undefined;
  }

  let eventId: string | undefined;

  withScope((scope) => {
    scope.setLevel(context.severity || "error");

    scope.setTag("build_version", metadata.buildVersion);
    scope.setTag("environment", metadata.environment);
    scope.setTag("dns_host", metadata.dnsHost);
    scope.setTag("execution_side", metadata.executionSide);
    scope.setTag("user_country", metadata.userCountry);
    scope.setTag("user_device", metadata.userDevice);
    scope.setTag("user_browser", metadata.userBrowser);
    scope.setTag("user_os", metadata.userOs);
    scope.setTag("user_platform", metadata.userPlatform);
    scope.setTag("runtime", runtime);
    scope.setTag("handled", handled ? "true" : "false");
    scope.setTag("expected", expected ? "true" : "false");

    if (context.source) scope.setTag("source", context.source);
    if (context.feature) scope.setTag("feature", context.feature);
    if (context.action) scope.setTag("action", context.action);
    if (context.component) scope.setTag("component", context.component);
    if (context.route) scope.setTag("route", context.route);
    if (context.method) scope.setTag("method", context.method);
    if (context.suppressionReason) {
      scope.setTag("suppression_reason", context.suppressionReason);
    }
    if (typeof context.statusCode === "number") {
      scope.setTag("status_code", String(context.statusCode));
    }

    for (const [key, value] of Object.entries(safeTags)) {
      scope.setTag(key, safeTagValue(value));
    }

    scope.setContext("error_metadata", {
      build_version: metadata.buildVersion,
      environment: metadata.environment,
      dns_host: metadata.dnsHost,
      execution_side: metadata.executionSide,
      error_time_iso: metadata.errorTimeIso,
      user_id: metadata.userId,
      user_email: metadata.userEmail,
      user_ip: metadata.userIp,
      user_browser: metadata.userBrowser,
      user_device: metadata.userDevice,
      user_os: metadata.userOs,
      user_platform: metadata.userPlatform,
      user_country: metadata.userCountry,
    });

    scope.setContext("error_context", {
      source: context.source,
      feature: context.feature,
      action: context.action,
      component: context.component,
      route: context.route,
      method: context.method,
      status_code: context.statusCode,
      extra: safeExtra,
    });
    scope.setContext("error_classification", {
      runtime,
      handled,
      expected,
      suppression_reason: context.suppressionReason,
    });

    const userId =
      metadata.userId !== "anonymous" ? metadata.userId : undefined;
    const userEmail =
      metadata.userEmail !== "anonymous" ? metadata.userEmail : undefined;
    const userIp = metadata.userIp !== "unknown" ? metadata.userIp : undefined;

    if (userId || userEmail || userIp) {
      scope.setUser({
        id: userId,
        email: userEmail,
        ip_address: userIp,
      });
    }

    eventId = captureException(normalizedError);
  });

  return eventId;
}
